import { useState } from 'react';
import { Finding } from '../types';

interface FindingsTableProps {
  findings: Finding[];
}

function FindingsTable({ findings }: FindingsTableProps) {
  const [filter, setFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');

  const filteredFindings =
    filter === 'all' ? findings : findings.filter((f) => f.severity === filter);

  const severityColors = {
    high: 'bg-red-100 text-red-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-blue-100 text-blue-800',
  };

  return (
    <div className="space-y-4">
      {/* Filter buttons */}
      <div className="flex space-x-2">
        {(['all', 'high', 'medium', 'low'] as const).map((severity) => (
          <button
            key={severity}
            onClick={() => setFilter(severity)}
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              filter === severity
                ? 'bg-gray-800 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {severity === 'all'
              ? '全部'
              : severity === 'high'
              ? '高风险'
              : severity === 'medium'
              ? '中风险'
              : '低风险'}
            {severity !== 'all' && (
              <span className="ml-1">
                ({findings.filter((f) => f.severity === severity).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Findings list */}
      <div className="space-y-3">
        {filteredFindings.map((finding, index) => (
          <div
            key={index}
            className="border rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold ${severityColors[finding.severity]}`}
                  >
                    {finding.severity === 'high'
                      ? '高风险'
                      : finding.severity === 'medium'
                      ? '中风险'
                      : '低风险'}
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {finding.detector}
                  </span>
                </div>

                <p className="text-sm text-gray-700 mb-1">
                  <span className="font-medium">规则:</span> {finding.rule}
                </p>

                <p className="text-sm text-gray-600 mb-1">
                  <span className="font-medium">证据:</span> {finding.evidence}
                </p>

                {finding.location && (
                  <p className="text-sm text-gray-500">
                    <span className="font-medium">位置:</span>{' '}
                    {finding.file}
                    {finding.sheet && ` / ${finding.sheet}`}
                    {finding.location && ` / ${finding.location}`}
                  </p>
                )}

                {finding.false_positive_context && (
                  <p className="text-sm text-gray-400 mt-2">
                    <span className="font-medium">可能误报:</span>{' '}
                    {finding.false_positive_context}
                  </p>
                )}

                {/* Raw Data Snapshot */}
                {finding.raw_data_snapshot && (
                  <div className="mt-4 bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">
                      📊 原始数据验证
                    </h4>

                    {/* Column Comparison Table */}
                    {finding.raw_data_snapshot.type === 'column_comparison' && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="px-3 py-2 text-left font-medium text-gray-700">行号</th>
                              {finding.raw_data_snapshot.columns?.map((col) => (
                                <th key={col} className="px-3 py-2 text-left font-medium text-gray-700">{col}</th>
                              ))}
                              {finding.raw_data_snapshot.sample_data?.computed_offset && (
                                <th className="px-3 py-2 text-left font-medium text-gray-700">计算偏移量</th>
                              )}
                              {finding.raw_data_snapshot.sample_data?.computed_ratio && (
                                <th className="px-3 py-2 text-left font-medium text-gray-700">计算比率</th>
                              )}
                              {finding.raw_data_snapshot.sample_data?.predicted_y && (
                                <th className="px-3 py-2 text-left font-medium text-gray-700">预测值</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {finding.raw_data_snapshot.sample_data?.row_indices?.map((rowIdx, idx) => (
                              <tr key={idx} className="bg-yellow-50">
                                <td className="px-3 py-2 font-mono text-gray-900">{rowIdx + 1}</td>
                                {finding.raw_data_snapshot!.columns?.map((col) => (
                                  <td key={col} className="px-3 py-2 font-mono text-gray-900">
                                    {typeof finding.raw_data_snapshot!.sample_data[col] === 'number'
                                      ? (finding.raw_data_snapshot!.sample_data[col] as number).toFixed(6)
                                      : finding.raw_data_snapshot!.sample_data[col]?.[idx]?.toFixed(6) || '-'}
                                  </td>
                                ))}
                                {finding.raw_data_snapshot!.sample_data?.computed_offset && (
                                  <td className="px-3 py-2 font-mono text-gray-900">
                                    {finding.raw_data_snapshot!.sample_data.computed_offset[idx]?.toFixed(6) || '-'}
                                  </td>
                                )}
                                {finding.raw_data_snapshot!.sample_data?.computed_ratio && (
                                  <td className="px-3 py-2 font-mono text-gray-900">
                                    {finding.raw_data_snapshot!.sample_data.computed_ratio[idx]?.toFixed(6) || '-'}
                                  </td>
                                )}
                                {finding.raw_data_snapshot!.sample_data?.predicted_y && (
                                  <td className="px-3 py-2 font-mono text-gray-900">
                                    {finding.raw_data_snapshot!.sample_data.predicted_y[idx]?.toFixed(6) || '-'}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Single Column Table */}
                    {finding.raw_data_snapshot.type === 'single_column' && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="px-3 py-2 text-left font-medium text-gray-700">行号</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">值</th>
                              {finding.raw_data_snapshot.sample_data?.differences && (
                                <th className="px-3 py-2 text-left font-medium text-gray-700">差值</th>
                              )}
                              {finding.raw_data_snapshot.sample_data?.ratios && (
                                <th className="px-3 py-2 text-left font-medium text-gray-700">比率</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {finding.raw_data_snapshot.sample_data?.row_indices?.map((rowIdx, idx) => (
                              <tr key={idx} className="bg-yellow-50">
                                <td className="px-3 py-2 font-mono text-gray-900">{rowIdx + 1}</td>
                                <td className="px-3 py-2 font-mono text-gray-900">
                                  {finding.raw_data_snapshot!.sample_data?.values?.[idx]?.toFixed(6) || '-'}
                                </td>
                                {finding.raw_data_snapshot!.sample_data?.differences && (
                                  <td className="px-3 py-2 font-mono text-gray-900">
                                    {finding.raw_data_snapshot!.sample_data.differences[idx] !== null
                                      ? finding.raw_data_snapshot!.sample_data.differences[idx]?.toFixed(6)
                                      : '-'}
                                  </td>
                                )}
                                {finding.raw_data_snapshot!.sample_data?.ratios && (
                                  <td className="px-3 py-2 font-mono text-gray-900">
                                    {finding.raw_data_snapshot!.sample_data.ratios[idx] !== null
                                      ? finding.raw_data_snapshot!.sample_data.ratios[idx]?.toFixed(6)
                                      : '-'}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Statistics Summary */}
                    {finding.raw_data_snapshot.statistics && (
                      <div className="mt-3 bg-blue-50 rounded px-3 py-2 text-sm">
                        <strong className="text-blue-800">统计摘要:</strong>{' '}
                        <span className="text-blue-700">
                          {Object.entries(finding.raw_data_snapshot.statistics).map(([key, value], idx, arr) => (
                            <span key={key}>
                              {key}: {typeof value === 'number' ? value.toFixed(4) : String(value)}
                              {idx < arr.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </span>
                      </div>
                    )}

                    <p className="text-xs text-gray-500 mt-2 italic">
                      💡 提示: 以上数据可直接在原始文件中定位验证。行号为 1-based 索引。
                    </p>
                  </div>
                )}

                {/* Image Evidence */}
                {finding.image_evidence && finding.image_evidence.length >= 2 && (
                  <div className="mt-4 bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">
                      🖼️ 图片对比
                    </h4>
                    <div className="flex flex-wrap gap-4">
                      {finding.image_evidence.map((img, idx) => (
                        <div key={idx} className="flex-1 min-w-[300px] text-center">
                          <img
                            src={`/uploads${img.path.replace('/app/uploads', '')}`}
                            alt={img.label}
                            className="max-w-full border-2 border-gray-200 rounded"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                          <div className="text-xs text-gray-600 mt-2 font-medium">
                            {img.label}: {img.filename}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2 italic">
                      💡 提示: 对比图片的边缘/点云结构相似度。Hamming 距离越小表示相似度越高。
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {filteredFindings.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            没有发现项
          </div>
        )}
      </div>
    </div>
  );
}

export default FindingsTable;
